import { Phone, Mail, MapPin, MessageCircle, Receipt, Clock } from "lucide-react";
import { COMPANY } from "../lib/constants";

export const Contact = () => {
  const whatsappUrl = `https://wa.me/${COMPANY.whatsapp}?text=${encodeURIComponent(
    "Hi Tech Thrissur, I'm interested in solar solutions."
  )}`;
  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-20 lg:py-28 bg-gradient-to-b from-[#F8FAFC] to-white"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="chapter-label tt-reveal" data-testid="contact-chapter-label"><span>06 / LET’S BEGIN</span><span>YOUR BRIGHTER FUTURE STARTS HERE</span></div>
        <div className="section-intro tt-reveal"><div><h2 className="eyebrow" data-testid="contact-heading">GET IN TOUCH</h2><p className="section-statement" data-testid="contact-statement">The sun is ready.<br /><span>Are you?</span></p></div><p>Tell us about your roof, your plans, your possibilities. A better energy future starts with a conversation.</p></div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left – Info */}
          <div className="tt-reveal bg-white rounded-3xl p-8 lg:p-10 border border-slate-100 shadow-[0_20px_60px_rgba(15,76,129,0.07)]">
            <h3 className="font-display text-2xl font-bold text-[#0F4C81] mb-2">
              {COMPANY.name}
            </h3>
            <p className="text-[#F6B800] font-semibold italic mb-7">{COMPANY.tagline}</p>

            <ul className="space-y-5">
              <li className="flex items-start gap-4" data-testid="contact-address">
                <div className="w-11 h-11 rounded-xl bg-[#0F4C81]/10 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-[#0F4C81]" />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Address</p>
                  <p className="text-slate-800 font-medium leading-relaxed">
                    {COMPANY.address.line1}<br />
                    {COMPANY.address.line2}<br />
                    <span className="text-slate-500 text-sm">{COMPANY.address.state}</span>
                  </p>
                </div>
              </li>

              <li className="flex items-start gap-4" data-testid="contact-gstin">
                <div className="w-11 h-11 rounded-xl bg-[#2E8B57]/10 flex items-center justify-center flex-shrink-0">
                  <Receipt className="w-5 h-5 text-[#2E8B57]" />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">GSTIN</p>
                  <p className="text-slate-800 font-medium font-mono">{COMPANY.gstin}</p>
                </div>
              </li>

              <li className="flex items-start gap-4" data-testid="contact-phones">
                <div className="w-11 h-11 rounded-xl bg-[#F6B800]/15 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5 text-[#0F4C81]" />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Phone</p>
                  {COMPANY.phones.map((p) => (
                    <a
                      key={p}
                      data-testid={`contact-phone-${p.replace(/\D/g, '')}`}
                      href={`tel:${p.replace(/\s/g, "")}`}
                      className="block text-slate-800 font-medium hover:text-[#0F4C81] transition-colors"
                    >
                      {p}
                    </a>
                  ))}
                </div>
              </li>

              <li className="flex items-start gap-4" data-testid="contact-email">
                <div className="w-11 h-11 rounded-xl bg-[#0F4C81]/10 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-[#0F4C81]" />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Email</p>
                  <a
                    data-testid="contact-email-link"
                    href={`mailto:${COMPANY.email}`}
                    className="text-slate-800 font-medium hover:text-[#0F4C81] transition-colors break-all"
                  >
                    {COMPANY.email}
                  </a>
                </div>
              </li>

              <li className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-[#2E8B57]/10 flex items-center justify-center flex-shrink-0">
                  <Clock className="w-5 h-5 text-[#2E8B57]" />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Business Hours</p>
                  <p className="text-slate-800 font-medium" data-testid="contact-business-hours">Mon – Sat · 9:00 AM – 7:00 PM</p>
                </div>
              </li>
            </ul>
          </div>

          {/* Right – Action buttons */}
          <div className="tt-reveal flex flex-col gap-5">
            <div className="bg-gradient-to-br from-[#0F4C81] to-[#0c3d68] rounded-3xl p-8 lg:p-10 text-white shadow-[0_25px_60px_rgba(15,76,129,0.25)] relative overflow-hidden">
              <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-[#F6B800]/20 blur-3xl" />
              <div className="relative">
                <h3 className="font-display text-2xl sm:text-3xl font-bold mb-3">
                  Speak to us today
                </h3>
                <p className="text-white/80 mb-7 leading-relaxed">
                  Call our solar experts directly or tell us about your project
                  on WhatsApp. We’re here to help you find the right solution.
                </p>
                <div className="space-y-3">
                  <a
                    href={`tel:${COMPANY.phones[0].replace(/\s/g, "")}`}
                    data-testid="contact-call-primary"
                    className="w-full inline-flex items-center justify-center gap-3 bg-[#F6B800] text-[#0F4C81] font-bold px-6 py-5 rounded-2xl hover:bg-yellow-300 hover:scale-[1.02] transition-all duration-300 shadow-lg text-base"
                  >
                    <Phone className="w-5 h-5" />
                    Call {COMPANY.phones[0]}
                  </a>
                  <a
                    href={`tel:${COMPANY.phones[1].replace(/\s/g, "")}`}
                    data-testid="contact-call-secondary"
                    className="w-full inline-flex items-center justify-center gap-3 bg-white/10 border border-white/25 backdrop-blur-md text-white font-semibold px-6 py-4 rounded-2xl hover:bg-white hover:text-[#0F4C81] transition-all duration-300"
                  >
                    <Phone className="w-5 h-5" />
                    Call {COMPANY.phones[1]}
                  </a>
                </div>
              </div>
            </div>

            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp"
              className="group relative bg-[#25D366] text-white rounded-3xl p-7 lg:p-8 flex items-center gap-5 shadow-[0_20px_50px_rgba(37,211,102,0.3)] hover:scale-[1.02] hover:bg-[#1fb556] transition-all duration-300 overflow-hidden"
            >
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.2),transparent_60%)]" />
              <div className="w-16 h-16 rounded-2xl bg-white/15 backdrop-blur-md border border-white/30 flex items-center justify-center flex-shrink-0 relative">
                <MessageCircle className="w-8 h-8" strokeWidth={2.2} />
              </div>
              <div className="relative flex-1">
                <p className="text-white/85 text-xs uppercase tracking-[0.18em] font-semibold mb-1">
                  Quick Response
                </p>
                <p className="font-display text-xl sm:text-2xl font-bold">
                  Chat on WhatsApp
                </p>
                <p className="text-white/85 text-sm mt-0.5">{COMPANY.phones[0]}</p>
              </div>
              <div className="relative w-12 h-12 rounded-full bg-white/15 flex items-center justify-center group-hover:translate-x-1 transition-transform">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14M13 5l7 7-7 7"/>
                </svg>
              </div>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};
